import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Button, IconButton } from "@/components/ui/Button";
import { BRAND, statusColorOf } from "@/lib/tokens";
import { fmtMonthYear, localDayKey } from "@/lib/format";
import { t } from "@/lib/strings";
import type { ViewBoard, ViewColumn, ViewRow } from "@/lib/boardView";

interface CalendarViewProps {
  board: ViewBoard;
  onRowClick: (row: ViewRow) => void;
  today?: string;
  // F6: drag a task to another day → write its due date (dayKey = YYYY-MM-DD).
  // Undefined disables drag (guests / read-only).
  onReschedule?: (rowId: string, dayKey: string) => void;
}

type CalMode = "month" | "week";

// Day cells are keyed with `localDayKey` (lib/format): the grid Date objects are
// built in local time, so a UTC (toISOString) key would read back the previous
// calendar day at +TZ offsets and shift every task one day to the right.

// Monday of the week containing `d` (local time).
function weekStart(d: Date): Date {
  const dow = (d.getDay() + 6) % 7; // Mon = 0
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  s.setDate(s.getDate() - dow);
  return s;
}

export function CalendarView({
  board,
  onRowClick,
  today = localDayKey(new Date()),
  onReschedule,
}: CalendarViewProps) {
  const todayDate = useMemo(() => new Date(`${today}T00:00:00Z`), [today]);
  const [mode, setMode] = useState<CalMode>("month");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const dragEnabled = Boolean(onReschedule);
  // The row being dragged → rendered in a DragOverlay so it follows the cursor
  // UNCLIPPED (day cells are overflow:hidden; without the overlay the chip just
  // dims in place and the drag reads as broken).
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeRow = activeId ? board.rows.find((r) => r.id === activeId) : null;

  // Measure a month cell's height so we can fit as many chips as the row allows
  // (all 6 rows are equal 1fr, so grid height / 6 = one cell). Reflows on resize.
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [cellH, setCellH] = useState(0);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => setCellH(el.clientHeight / (mode === "week" ? 1 : 6));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mode]);
  // How many chips fit in a month cell (chip+gap ≈ 22px; ~32px for the day-number
  // header + cell padding). `fitWithMore` reserves a line for the "+N" row so it
  // is never clipped. Falls back pre-measure.
  const CHIP_LINE = 22;
  const HEADER_RESERVE = 32;
  const MORE_LINE = 20;
  const fitAll = cellH > 0 ? Math.max(1, Math.floor((cellH - HEADER_RESERVE) / CHIP_LINE)) : 3;
  const fitWithMore =
    cellH > 0 ? Math.max(1, Math.floor((cellH - HEADER_RESERVE - MORE_LINE) / CHIP_LINE)) : 2;

  function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    if (!onReschedule) return;
    const rowId = String(e.active.id);
    const dayKey = e.over?.id ? String(e.over.id) : null;
    if (!dayKey) return;
    const row = board.rows.find((r) => r.id === rowId);
    if (row && row.due === dayKey) return; // dropped on its own day — no-op
    onReschedule(rowId, dayKey);
  }
  // A reference date; month mode uses its month, week mode uses its week.
  const [anchor, setAnchor] = useState<Date>(() => new Date(todayDate));

  const grid = useMemo(() => {
    const days: Date[] = [];
    if (mode === "week") {
      const start = weekStart(anchor);
      for (let i = 0; i < 7; i += 1) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d);
      }
      return days;
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const firstDow = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(1 - firstDow);
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [anchor, mode]);

  const statusCol = board.columns.find((c) => c.type === "status");
  const rowsByDate = useMemo(() => {
    const m = new Map<string, ViewRow[]>();
    board.rows.forEach((r) => {
      if (!r.due) return;
      if (!m.has(r.due)) m.set(r.due, []);
      m.get(r.due)!.push(r);
    });
    return m;
  }, [board.rows]);

  const isWeek = mode === "week";
  const wkStart = grid[0];
  const wkEnd = grid[grid.length - 1];
  const label =
    isWeek && wkStart && wkEnd
      ? `${wkStart.toLocaleDateString("de-DE", { day: "numeric", month: "short" })} – ${wkEnd.toLocaleDateString(
          "de-DE",
          { day: "numeric", month: "short", year: "numeric" },
        )}`
      : fmtMonthYear(anchor);

  const goToday = () => setAnchor(new Date(todayDate));
  const step = (dir: -1 | 1) => {
    if (isWeek) {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + dir * 7);
      setAnchor(d);
    } else {
      setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
    }
  };

  const tabStyle = (active: boolean) => ({
    padding: "5px 12px",
    borderRadius: 7,
    border: "none",
    background: active ? BRAND.blue : "transparent",
    color: active ? "#fff" : "var(--ink-3)",
    fontSize: 12.5,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  });

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px 12px",
          borderBottom: "1px solid var(--hairline-soft)",
        }}
      >
        <Button variant="ghost" size="sm" onClick={goToday}>
          {t.today}
        </Button>
        <IconButton icon="chevLeft" size={28} onClick={() => step(-1)} />
        <IconButton icon="chevRight" size={28} onClick={() => step(1)} />
        <h2
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            color: "var(--ink-1)",
            textTransform: "capitalize",
          }}
        >
          {label}
        </h2>
        <div style={{ flex: 1 }}></div>
        <div
          style={{
            display: "inline-flex",
            gap: 2,
            background: "var(--surface-soft)",
            borderRadius: 9,
            padding: 2,
          }}
        >
          <button type="button" style={tabStyle(!isWeek)} onClick={() => setMode("month")}>
            {t.calMonth}
          </button>
          <button type="button" style={tabStyle(isWeek)} onClick={() => setMode("week")}>
            {t.calWeek}
          </button>
        </div>
        <span style={{ fontSize: 12, color: "var(--ink-3)", marginLeft: 8 }}>
          {board.rows.filter((r) => r.due).length} {t.rowsWithDate}
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            borderBottom: "1px solid var(--hairline)",
            background: "var(--surface-soft)",
          }}
        >
          {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d, i) => (
            <div
              key={d}
              style={{
                padding: "8px 12px",
                fontSize: 11,
                fontWeight: 600,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                borderRight: i < 6 ? "1px solid var(--hairline-soft)" : "none",
                opacity: i >= 5 ? 0.6 : 1,
              }}
            >
              {d}
            </div>
          ))}
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div
            ref={gridRef}
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gridTemplateRows: isWeek ? "minmax(0, 1fr)" : "repeat(6, minmax(0, 1fr))",
              minHeight: 0,
            }}
          >
            {grid.map((d, i) => {
              const inMonth = isWeek || d.getMonth() === anchor.getMonth();
              const iso = localDayKey(d);
              const isToday = iso === today;
              const rows = rowsByDate.get(iso) ?? [];
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              // Week mode: one tall row → show all tasks, scroll if needed.
              // Month mode: fit as many chips as the cell height allows; on
              // overflow the last slot becomes a "+N" whose hover tooltip lists
              // the hidden entries.
              const hasOverflow = !isWeek && rows.length > fitAll;
              const visibleRows = isWeek ? rows : rows.slice(0, hasOverflow ? fitWithMore : fitAll);
              const hiddenRows = rows.slice(visibleRows.length);
              return (
                <DayCell
                  key={i}
                  iso={iso}
                  dropEnabled={dragEnabled}
                  cellStyle={{
                    borderRight: i % 7 < 6 ? "1px solid var(--hairline-soft)" : "none",
                    borderBottom: !isWeek && i < 35 ? "1px solid var(--hairline-soft)" : "none",
                    background: isWeekend && inMonth ? "var(--surface-soft)" : "transparent",
                    opacity: inMonth ? 1 : 0.45,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0 2px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 22,
                        height: 22,
                        padding: "0 6px",
                        borderRadius: 999,
                        background: isToday ? BRAND.orange : "transparent",
                        color: isToday ? "#fff" : "var(--ink-2)",
                        fontSize: 12,
                        fontWeight: isToday ? 600 : 500,
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {d.getDate()}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      minHeight: 0,
                      flex: 1,
                      overflowY: isWeek ? "auto" : "hidden",
                    }}
                  >
                    {visibleRows.map((r) => (
                      <TaskChip
                        key={r.id}
                        row={r}
                        statusCol={statusCol}
                        draggable={dragEnabled}
                        onRowClick={onRowClick}
                      />
                    ))}
                  </div>
                  {/* Pinned OUTSIDE the clipped chip list so the "+N" is never
                      cut off; hover lists the hidden entries. */}
                  {hasOverflow ? (
                    <span
                      title={hiddenRows.map((r) => `• ${r.title || "—"}`).join("\n")}
                      style={{
                        padding: "1px 6px 2px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--ink-3)",
                        cursor: "default",
                        flexShrink: 0,
                      }}
                    >
                      +{hiddenRows.length} weitere
                    </span>
                  ) : null}
                </DayCell>
              );
            })}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeRow ? <ChipPreview row={activeRow} statusCol={statusCol} /> : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}

// F6: a droppable day cell. Highlights while a task hovers over it.
function DayCell({
  iso,
  dropEnabled,
  cellStyle,
  children,
}: {
  iso: string;
  dropEnabled: boolean;
  cellStyle: React.CSSProperties;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: iso, disabled: !dropEnabled });
  return (
    <div
      ref={setNodeRef}
      style={{
        ...cellStyle,
        padding: "6px 6px 4px",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        boxShadow: isOver ? `inset 0 0 0 2px ${BRAND.blue}` : "none",
        ...(isOver ? { background: "rgba(0,89,155,0.06)" } : null),
      }}
    >
      {children}
    </div>
  );
}

// The chip rendered in the DragOverlay while dragging — a static (non-draggable)
// clone that follows the cursor unclipped, so cross-cell drags are visible.
function ChipPreview({ row, statusCol }: { row: ViewRow; statusCol: ViewColumn | undefined }) {
  const opt = statusCol?.options?.find((o) => o.id === row.status);
  const tint = statusColorOf(opt?.color);
  return (
    <div
      className="flow-status-tint"
      style={
        {
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 6px",
          borderRadius: 5,
          "--pill-bg": tint.bg,
          "--pill-fg": tint.fg,
          "--pill-dot": tint.dot,
          cursor: "grabbing",
          boxShadow: "0 6px 18px rgba(0,0,0,0.22)",
          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        } as React.CSSProperties
      }
    >
      <span
        style={{ width: 5, height: 5, borderRadius: 99, background: tint.dot, flexShrink: 0 }}
      />
      <span style={{ fontSize: 11, fontWeight: 500, color: "inherit", whiteSpace: "nowrap" }}>
        {row.title}
      </span>
    </div>
  );
}

// F6: a calendar task chip — draggable (to reschedule) when enabled; a plain
// click still opens the row (8px drag activation distance).
function TaskChip({
  row,
  statusCol,
  draggable,
  onRowClick,
}: {
  row: ViewRow;
  statusCol: ViewColumn | undefined;
  draggable: boolean;
  onRowClick: (row: ViewRow) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: row.id,
    disabled: !draggable,
  });
  const opt = statusCol?.options?.find((o) => o.id === row.status);
  const tint = statusColorOf(opt?.color);
  return (
    <button
      ref={setNodeRef}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      onClick={() => onRowClick(row)}
      title={row.title}
      className="flow-status-tint"
      style={
        {
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 6px",
          border: "none",
          borderRadius: 5,
          // bg/fg come from .flow-status-tint so dark mode restyles them (translucent
          // chip + bright text) instead of leaving a light pastel island.
          "--pill-bg": tint.bg,
          "--pill-fg": tint.fg,
          "--pill-dot": tint.dot,
          cursor: draggable ? "grab" : "pointer",
          textAlign: "left",
          overflow: "hidden",
          flexShrink: 0,
          opacity: isDragging ? 0.4 : 1,
          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        } as React.CSSProperties
      }
    >
      <span
        style={{ width: 5, height: 5, borderRadius: 99, background: tint.dot, flexShrink: 0 }}
      ></span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.title}
      </span>
    </button>
  );
}
